import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <div className="pt-20">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-20 text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-6">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">文章未找到</h1>
          <p className="text-foreground-500 mb-8">抱歉，您要查看的文章不存在或已被移除。</p>
          <Link
            to="/categories"
            className="inline-flex items-center px-6 py-3 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回分类浏览
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const category = categories.find((c) => c.slug === article.categorySlug);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://shaoshangliang.cn/' },
          { '@type': 'ListItem', position: 2, name: '分类浏览', item: 'https://shaoshangliang.cn/categories' },
          { '@type': 'ListItem', position: 3, name: article.category, item: `https://shaoshangliang.cn/categories?tab=${article.categorySlug}` },
          { '@type': 'ListItem', position: 4, name: article.title },
        ],
      },
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        author: {
          '@type': 'Person',
          name: article.author,
        },
        datePublished: article.publishDate,
        image: article.image,
        articleSection: article.category,
        publisher: {
          '@type': 'Organization',
          name: '久久爱',
        },
      },
    ],
  };

  return (
    <article className="pt-20">
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-8">
        <Breadcrumb
          items={[
            { name: '首页', path: '/' },
            { name: '分类浏览', path: '/categories' },
            { name: article.category, path: `/categories?tab=${article.categorySlug}` },
            { name: article.title },
          ]}
        />

        <header className="mt-8 mb-10">
          <div className="flex items-center gap-2 mb-4">
            <Link
              to={`/categories?tab=${article.categorySlug}`}
              className="px-3 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors"
            >
              {article.category}
            </Link>
            {article.tags.map((tag) => (
              <span key={tag} className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700">
                {tag}
              </span>
            ))}
          </div>

          <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-6">
            {article.title}
          </h1>

          <div className="flex items-center gap-4 text-sm text-foreground-500 pb-8 border-b border-background-200/70">
            <div className="flex items-center gap-2">
              <img
                src={article.authorAvatar}
                alt={article.author}
                className="w-8 h-8 rounded-full object-cover"
              />
              <span className="font-medium text-foreground-700">{article.author}</span>
            </div>
            <span className="text-foreground-300">|</span>
            <time dateTime={article.publishDate}>{article.publishDate}</time>
            <span className="text-foreground-300">|</span>
            <span>{article.readTime} 分钟阅读</span>
          </div>
        </header>

        <div className="mb-10 rounded-xl overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            title={`${article.title} - 久久爱${article.category}`}
            className="w-full h-auto object-cover"
          />
        </div>

        <div
          className="prose-custom text-foreground-800 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        {relatedArticles.length > 0 && (
          <section className="mt-16 pt-10 border-t border-background-200/70">
            <h3 className="text-xl font-heading font-bold text-foreground-950 mb-6">
              相关文章
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {relatedArticles.map((related) => (
                <Link
                  key={related.id}
                  to={`/article/${related.id}`}
                  className="group cursor-pointer"
                >
                  <div className="h-36 rounded-lg overflow-hidden mb-3">
                    <img
                      src={related.image}
                      alt={related.title}
                      title={`${related.title} - 久久爱`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {related.title}
                  </h4>
                  <p className="text-xs text-foreground-500 mt-1">{related.readTime} 分钟阅读</p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      <style>{`
        .prose-custom h2 {
          font-family: var(--font-heading);
          font-size: 1.5rem;
          font-weight: 700;
          color: oklch(var(--foreground-950));
          margin-top: 2.5rem;
          margin-bottom: 1rem;
          line-height: 1.4;
        }
        .prose-custom h3 {
          font-family: var(--font-heading);
          font-size: 1.2rem;
          font-weight: 600;
          color: oklch(var(--foreground-900));
          margin-top: 2rem;
          margin-bottom: 0.75rem;
        }
        .prose-custom p {
          font-size: 1rem;
          line-height: 1.85;
          margin-bottom: 1.25rem;
          color: oklch(var(--foreground-700));
        }
        .prose-custom ul, .prose-custom ol {
          margin-bottom: 1.25rem;
          padding-left: 1.5rem;
        }
        .prose-custom li {
          font-size: 1rem;
          line-height: 1.85;
          color: oklch(var(--foreground-700));
          margin-bottom: 0.5rem;
        }
        .prose-custom strong {
          color: oklch(var(--foreground-900));
          font-weight: 600;
        }
        .prose-custom a {
          color: oklch(var(--primary-500));
          text-decoration: underline;
        }
        @media (max-width: 768px) {
          .prose-custom h2 { font-size: 1.25rem; }
          .prose-custom p, .prose-custom li { font-size: 0.9375rem; }
        }
      `}</style>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </article>
  );
}